<script setup lang="ts">
import type { Schemas } from '#shopware'
import type { ListingItemProduct } from '~/types/ListingItems'

const props = defineProps<{
    content: Schemas['CmsElementProductTabSlider']
}>()

const activeTab = ref(0)

const toListingItems = (products: Schemas['Product'][]): ListingItemProduct[] =>
    products.map((product, index) => ({ pos: index, type: 'product', data: product }))

type TabState = { name: string; products: ListingItemProduct[]; loaded: boolean }

const tabProducts = ref<Record<number, TabState>>({
    0: {
        name: props.content?.config?.tabs?.value?.[0]?.name?.value ?? '',
        products: toListingItems(props.content?.data?.products ?? []),
        loaded: true,
    },
})

const tabs = computed(() => props.content?.config?.tabs?.value ?? [])

const { routeName, foreignKey } = useNavigationContext()
const { apiClient } = useShopwareContext()

function setTab(index: number, products: ListingItemProduct[]) {
    tabProducts.value = {
        ...tabProducts.value,
        [index]: {
            name: tabs.value[index]?.name?.value ?? '',
            products,
            loaded: true,
        },
    }
}

async function switchTab(index: number) {
    activeTab.value = index

    // Scroll the clicked tab into view smoothly
    nextTick(() => {
        const tabElements = tabNavigationScrollContainer.value?.querySelectorAll('.flex-none')
        if (tabElements?.[index]) {
            (tabElements[index] as HTMLElement).scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'start'
            })
        }
    })

    // First tab is server-rendered; every other tab is loaded once and cached.
    // `loaded` is tracked explicitly so a tab that resolves to zero products (or
    // fails) shows an empty state instead of spinning forever.
    if (tabProducts.value[index]?.loaded) return

    const slotId = props.content?.extensions?.a4gConfig?.slotId
    if (!slotId) {
        setTab(index, [])
        return
    }

    const query: { categoryId?: string; productId?: string } = {}
    if (routeName.value === 'frontend.navigation.page') {
        query.categoryId = foreignKey.value
    } else if (routeName.value === 'frontend.detail.page') {
        query.productId = foreignKey.value
    }

    try {
        const { data: result } = await apiClient.invoke(
            'readProductSliderTab post /product-slider/slot/{slotId}/tab/{tabIndex}',
            { pathParams: { slotId, tabIndex: index + 1 }, query }
        )
        setTab(index, toListingItems(result?.[0]?.data?.products ?? []))
    } catch (e) {
        console.error(e)
        setTab(index, [])
    }
}

const tabNavigationScrollContainer = ref<HTMLDivElement | null>(null)
const showScrollIndicator = ref(false)
const showLeftScrollIndicator = ref(false)

function checkScrollable() {
    if (tabNavigationScrollContainer.value) {
        const { scrollWidth, clientWidth, scrollLeft } = tabNavigationScrollContainer.value
        const isScrollable = scrollWidth > clientWidth
        const isAtEnd = scrollLeft + clientWidth >= scrollWidth - 1
        const isAtStart = scrollLeft <= 1

        showScrollIndicator.value = isScrollable && !isAtEnd
        showLeftScrollIndicator.value = isScrollable && !isAtStart
    }
}

watch(tabs, () => {
    if (tabs.value.length > 0) {
        nextTick(() => {
            // Use setTimeout to ensure DOM has fully rendered and layout is complete
            setTimeout(() => {
                checkScrollable()
            }, 100)
        })
    }
}, { immediate: true })

onMounted(() => {
    tabNavigationScrollContainer.value?.addEventListener('scroll', checkScrollable)
    window.addEventListener('resize', checkScrollable)
})

onUnmounted(() => {
    tabNavigationScrollContainer.value?.removeEventListener('scroll', checkScrollable)
    window.removeEventListener('resize', checkScrollable)
})
</script>

<template>
    <div>
        <div
            class="relative mt-3 w-auto"
        >
            <div
                v-if="tabs.length > 0"
                id="tab-navigation"
                ref="tabNavigationScrollContainer"
                class="tab-navigation flex flex-row flex-nowrap gap-8 py-3 md:py-0 overflow-x-auto no-scrollbar snap-x snap-proximity"
                role="group"
            >
                <div
                    v-for="(tab, index) in tabs"
                    v-show="tab.name?.value"
                    :key="index"
                    class="flex-none snap-start"
                >
                    <button
                        :class="activeTab === index
                            ? 'tab-active opacity-100 text-neutral border-b-2 border-neutral'
                            : 'text-base-content-subtle opacity-75'"
                        class="text-lg font-semibold py-4 hover:cursor-pointer hover:opacity-100 whitespace-nowrap"
                        type="button"
                        @click="switchTab(index)"
                    >
                        {{ tab.name?.value }}
                    </button>
                </div>
            </div>
            <!-- Left Scroll Indicator -->
            <div
                v-if="showLeftScrollIndicator"
                class="absolute left-0 top-0 bottom-0 w-12 pointer-events-none flex items-center justify-start pl-2"
                style="background: linear-gradient(to left, transparent 0%, white 70%)"
            >
                <MiscIcon name="chevron-left" />
            </div>
            <!-- Right Scroll Indicator -->
            <div
                v-if="showScrollIndicator"
                class="absolute right-0 top-0 bottom-0 w-12 pointer-events-none flex items-center justify-end pr-2"
                style="background: linear-gradient(to right, transparent 0%, white 70%)"
            >
                <MiscIcon name="chevron-right" />
            </div>
        </div>

        <div
            v-for="(tab, tabIndex) in tabs"
            :key="tabIndex"
        >
            <div
                v-show="activeTab === tabIndex"
                class="pt-2 pb-6 tab-slider transition-all duration-200"
            >
                <ProductSlider
                    v-if="(tabProducts[tabIndex]?.products?.length ?? 0) > 0"
                    :products="tabProducts[tabIndex]!.products"
                    :product-qty="content?.config?.productQty"
                    :slider-id="`${tab.name?.value}_${tabIndex}`"
                    :slider-title="`${tab.name?.value} - ${tabIndex}`"
                />
                <div
                    v-else-if="!tabProducts[tabIndex]?.loaded"
                    class="h-[400px] flex gap-3 items-center loading mx-auto"
                />
            </div>
        </div>
    </div>
</template>